/**
 * Пошук і показ цілей тура на сторінці: [data-tour="…"], лише видимі й не перекриті,
 * прокручування цілі у видиму область з урахуванням верхньої смуги й нижньої навігації.
 */

/** Позначка елементів шару навчання (затемнення, картка тура, кнопка «Навчання»): вони не «перекривають» ціль. */
export const TRAINING_LAYER_ATTR = 'data-training-layer';

/** Елемент показано: у документі, не в інертному піддереві, має розмір і не прихований стилями. */
export function isRendered(el: Element): boolean {
  if (!el.isConnected) return false;
  if (el.closest('[inert]')) return false;
  if (typeof el.checkVisibility === 'function' && !el.checkVisibility({ checkVisibilityCSS: true })) return false;
  const r = el.getBoundingClientRect();
  return r.width > 0 && r.height > 0;
}

function inViewport(r: DOMRect): boolean {
  return r.bottom > 0 && r.right > 0 && r.top < window.innerHeight && r.left < window.innerWidth;
}

/** true — у точці сама ціль; false — щось інше; null — невідомо (поза вікном або наш шар). */
function hitsTarget(el: Element, x: number, y: number): boolean | null {
  if (x < 0 || y < 0 || x >= window.innerWidth || y >= window.innerHeight) return null;
  const hit = document.elementFromPoint(x, y);
  if (!hit) return null;
  if (hit === el || el.contains(hit) || hit.contains(el)) return true;
  if (hit.closest(`[${TRAINING_LAYER_ATTR}]`)) return null;
  return false;
}

/** Ціль у вікні, але повністю закрита іншим шаром (наприклад, панеллю задачі). */
function isCovered(el: Element): boolean {
  const r = el.getBoundingClientRect();
  const inset = Math.min(6, r.width / 4, r.height / 4);
  const points: [number, number][] = [
    [r.left + r.width / 2, r.top + r.height / 2],
    [r.left + inset, r.top + inset],
    [r.right - inset, r.top + inset],
    [r.left + inset, r.bottom - inset],
    [r.right - inset, r.bottom - inset],
  ];
  let blocked = 0;
  for (const [x, y] of points) {
    const hit = hitsTarget(el, x, y);
    if (hit === true) return false;
    if (hit === false) blocked += 1;
  }
  return blocked > 0;
}

/**
 * Ціль кроку. Якщо елементів з однаковою назвою кілька (рядки, колонки, меню для телефона й комп’ютера),
 * береться перший видимий у вікні й не перекритий, інакше — перший видимий поза вікном (до нього прокрутимо).
 */
export function findTarget(name: string): HTMLElement | null {
  if (!name) return null;
  let list: HTMLElement[];
  try {
    list = Array.from(document.querySelectorAll<HTMLElement>(`[data-tour="${CSS.escape(name)}"]`));
  } catch {
    return null;
  }
  const rendered = list.filter(isRendered);
  if (!rendered.length) return null;
  const visible = rendered.find((el) => inViewport(el.getBoundingClientRect()) && !isCovered(el));
  if (visible) return visible;
  return rendered.find((el) => !inViewport(el.getBoundingClientRect())) ?? null;
}

/** Висота сталих смуг, які закривають сторінку зверху (верхня смуга) і знизу (нижня навігація телефона). */
function stickyInsets(): { top: number; bottom: number } {
  let top = 0;
  let bottom = 0;
  const topbar = document.getElementById('app-topbar');
  if (topbar && isRendered(topbar)) {
    const r = topbar.getBoundingClientRect();
    if (r.top <= 1) top = Math.max(0, r.bottom);
  }
  const nav = document.getElementById('app-bottomnav');
  if (nav && isRendered(nav)) {
    const r = nav.getBoundingClientRect();
    bottom = Math.max(0, window.innerHeight - r.top);
  }
  return { top, bottom };
}

/**
 * Прокручує ціль у видиму область (миттєво — рух довший за 200 мс не використовуємо).
 * Невелику ціль ставить по центру, велику — верхом під верхню смугу.
 */
export function ensureVisible(el: HTMLElement): void {
  const r = el.getBoundingClientRect();
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const { top, bottom } = stickyInsets();
  const avail = Math.max(1, vh - top - bottom);
  const large = r.height > avail * 0.7;
  const fitsX = r.width > vw ? r.right > 0 && r.left < vw : r.left >= 0 && r.right <= vw;
  const fitsY = large ? r.top >= top && r.top < vh - bottom - 80 : r.top >= top + 4 && r.bottom <= vh - bottom - 4;
  if (fitsX && fitsY) return;
  const prev = el.style.scrollMargin;
  el.style.scrollMargin = `${top + 12}px 16px ${bottom + 12}px 16px`;
  try {
    el.scrollIntoView({ block: large ? 'start' : 'center', inline: r.width > vw ? 'start' : 'nearest', behavior: 'auto' });
  } catch {
    /* старі браузери без параметрів — ціль лишається там, де є */
  } finally {
    el.style.scrollMargin = prev;
  }
}

/** Чи відкрито модальний діалог (під ним шар тура інертний — демонстрацію ставимо на паузу). */
export function hasOpenModal(): boolean {
  return !!document.querySelector('dialog[open]');
}
